import React from 'react';
import { fromUnixTime } from 'date-fns';
import { CollectOnSoundMarket } from '@components/basics/Badges/UrgencyBadge/CollectOnSound';
import { Ended } from '@components/basics/Badges/UrgencyBadge/Ended';
import { QuantityBadge } from '@components/basics/Badges/UrgencyBadge/QuantityBadge';
import { SoldOut } from '@components/basics/Badges/UrgencyBadge/SoldOut';
import { TextTimerBadge } from '@components/basics/Badges/UrgencyBadge/TextTimerBadge';
import { hasSaleStarted } from '../useMintState';
import type { ScheduleWithTierInfo } from './data';
import {
  getCollectedAmount,
  getMaxSupply,
  getMintExpiry,
  getSaleType,
  hasSaleEnded,
  isScheduleSoldOut,
} from './data';

export const Badges = ({
  schedule,
  availableOnSecondary,
}: {
  schedule: ScheduleWithTierInfo;
  availableOnSecondary: boolean;
}) => {
  const saleType = getSaleType(schedule);
  const isOpenEdition = saleType === 'open-edition';

  const isSaleRunning = hasSaleStarted({
    startTimestamp: schedule.startTime,
  });

  const isSoldOut = isScheduleSoldOut({
    schedule,
  });

  const saleEnded = hasSaleEnded({
    schedule,
  });

  const isPresale = schedule.mode === 'VERIFY_MERKLE';

  // Open Edition
  if (isOpenEdition) {
    if (isSaleRunning && !isSoldOut) {
      return (
        <TextTimerBadge
          label="Ends"
          expiryTimestamp={fromUnixTime(getMintExpiry(schedule)).getTime()}
        />
      );
    }

    if (!isPresale && saleEnded && availableOnSecondary) {
      return <CollectOnSoundMarket />;
    }

    if (isPresale && (saleEnded || isSoldOut)) {
      return <SoldOut />;
    }
  }

  if (isPresale && !isSoldOut) {
    if (saleEnded) {
      return <Ended />;
    }

    if (isSaleRunning) {
      return (
        <TextTimerBadge
          label="Ends"
          expiryTimestamp={fromUnixTime(getMintExpiry(schedule)).getTime()}
        />
      );
    }
  }

  const totalMinted = getCollectedAmount({ schedule });
  const maxValue = getMaxSupply(schedule);

  // Fixed Edition
  if (isSaleRunning && !isSoldOut) {
    return <QuantityBadge value={totalMinted} maxValue={maxValue} />;
  }

  if (!isPresale && isSoldOut && availableOnSecondary) {
    return <CollectOnSoundMarket />;
  }

  if (isSoldOut || saleEnded) return <SoldOut />;

  return null;
};
